
import { Component } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastrService } from 'ngx-toastr';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DirectivesModule } from 'src/app/directives.module';
import { PaginationComponent } from 'ngx-bootstrap/pagination';
import { PageChangedEvent } from 'ngx-bootstrap/pagination/public_api';
import { ActivatedRoute, Router } from '@angular/router';
import { TeacherJobsService } from 'src/app/services/api/consumer/jobs.service';
import { HttpClient } from '@angular/common/http';
import { co } from '@fullcalendar/core/internal-common';
import { PublicHeaderComponent } from 'src/app/layout/public-header/public-header.component';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-job-details',
  standalone: true,
  imports: [CommonModule, NgIf, FormsModule, ReactiveFormsModule, DirectivesModule, PublicHeaderComponent],
  templateUrl: './job-details.component.html',
  styleUrl: './job-details.component.scss'
})
export class JobDetailsComponent {
  jobId: string | null = null;
  fromApplied: boolean = false;
  applicantResumeUrl: string = '';
  applicantCoverLetterUrl: string = '';
  resumeFile: File | null = null;
  coverFile: File | null = null;
  resumeFileName: string | null = null;
  coverFileName: string | null = null;
  url: string | null = null;

  constructor(
    private ngxService: NgxUiLoaderService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    public bsModalRef: BsModalRef,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private TeacherJobsService: TeacherJobsService,
    private http: HttpClient,
    private meta: Meta,
    private titleService: Title
  ) { }
  job_details: any = [];
  isAuthenticated: boolean = false;
  fromTab: string = 'all';

  ngOnInit(): void {
    this.loadJobDetails();
  }

  loadJobDetails(): void {
    // Get fromTab param for back navigation
    const urlParams = new URLSearchParams(window.location.search);
    this.fromTab = urlParams.get('fromTab') || 'all';
    // Check for access_token and refresh_token in cookies for authentication
    const getCookie = (name: string) => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    };
    const accessToken = getCookie('access_token');
    const refreshToken = getCookie('refresh_token');
    this.isAuthenticated = !!(accessToken && refreshToken);
    this.jobId = this.route.snapshot.paramMap.get('id');
    const jobId = this.jobId; // Use the jobId extracted from the route
    // Get 'from' param from URL
    const fromParam = urlParams.get('from');
    // Reset stateful flags to ensure correct behavior on reload
    this.fromApplied = false;
    if (fromParam && fromParam.toLowerCase() === 'apply') {
      this.fromApplied = true;
    }

    if (!jobId) return;
    this.ngxService.start();
    this.TeacherJobsService.teacherJobDetailsAPI(jobId).subscribe({
      next: (res: any) => {
        if (res.status === false) {
          this.ngxService.stop();
          this.toastr.error(res.message || 'Job not found');
          return;
        } else {
          this.job_details = res.data;
          this.metaOGTags(this.job_details);
          // If from=applied, extract applicant resume/cover
          if (this.fromApplied && res.data.applicant) {
            this.applicantResumeUrl = res.data.applicant.resume || '';
            this.applicantCoverLetterUrl = res.data.applicant.cover_letter || '';
          }

          this.url = this.job_details.apply_through_site;
          if (this.url) {
            if (!/^https?:\/\//i.test(this.url)) {
              this.url = 'https://' + this.url;
            }
          }
        }
        this.ngxService.stop();
      },
      error: (err: any) => {
        this.ngxService.stop();
        this.toastr.error('Failed to load job details');
      }
    });
  }
  metaOGTags(job_details: any) {
    const schoolName = job_details?.school.title || ''; // get from your data
    const jobTitle = job_details?.job_title || '';     // get from your data
    const url = window.location.origin; // or your logic
    this.titleService.setTitle(`ClassCover Jobs | ${schoolName} - ${jobTitle}`);
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: 'ClassCover Jobs logo' }); // Use actual image URL
    this.meta.updateTag({ property: 'og:title', content: `ClassCover Jobs | ${schoolName} - ${jobTitle}` });
    this.meta.updateTag({ property: 'og:description', content: `${schoolName} has just posted a Contract job - '${jobTitle}'. Login or signup to view this job and switch on alerts to receive more jobs like this in the future. Australia. New jobs added every day.` });
    this.meta.updateTag({ property: 'og:url', content: `${url}/jobs/${schoolName}/${jobTitle}` });
  }
  uploadApplicantDocument(type: 'resume' | 'cover') {
    // Accept if any one file is uploaded, upload both if both are present
    const formData = new FormData();
    formData.append('job_id', this.job_details?.id);
    let hasFile = false;
    if (this.resumeFile) {
      formData.append('job_resume', this.resumeFile);
      hasFile = true;
    }
    if (this.coverFile) {
      formData.append('job_cover_letter', this.coverFile);
      hasFile = true;
    }
    if (!hasFile) {
      this.toastr.error('Please select a file to upload');
      return;
    }
    this.ngxService.start();
    this.TeacherJobsService.updateApplicantDocumentAPI(formData).subscribe({
      next: (res: any) => {
        this.toastr.success('Document uploaded successfully');
        this.ngxService.stop();
        // Optionally refresh applicant data
        this.loadJobDetails();

        // Clear file inputs and names after upload
        this.resumeFile = null;
        this.coverFile = null;
        this.resumeFileName = null;
        this.coverFileName = null;

        if (res.resume_url) {
          this.applicantResumeUrl = res.resume_url;
        }
        if (res.cover_url) {
          this.applicantCoverLetterUrl = res.cover_url;
        }
      },
      error: (err: any) => {
        this.toastr.error('Failed to upload document');
        this.ngxService.stop();
      }
    });
  }
  onFileSelected(event: any, type: 'resume' | 'cover') {
    const file = event.target.files[0];
    if (!file) return;
    if (type === 'resume') {
      this.resumeFile = file;
      this.resumeFileName = file.name;
    } else {
      this.coverFile = file;
      this.coverFileName = file.name;
    }
  }

  removeFile(type: 'resume' | 'cover', input: HTMLInputElement) {
    if (type === 'resume') {
      this.resumeFile = null;
      this.resumeFileName = null;
    } else {
      this.coverFile = null;
      this.coverFileName = null;
    }
    input.value = '';
  }

  backTo() {
    // Navigate back to the correct tab
    if (this.isAuthenticated) {
      if (this.fromTab === 'applied') {
        this.router.navigate(['/teacher/jobs'], { queryParams: { tab: 'applied' } });
      } else if (this.fromTab === 'saved') {
        this.router.navigate(['/teacher/jobs'], { queryParams: { tab: 'saved' } });
      } else if (this.fromTab === 'campAustraliaJobs') {
        this.router.navigate(['/teacher/jobs'], { queryParams: { tab: 'all', job_type: 'campAustraliaJobs' } });
      } else {
        this.router.navigate(['/teacher/jobs'], { queryParams: { tab: 'all' } });
      }
    } else {
      this.router.navigate(['/jobs']);
    }
  }

  quickApplyJob() {
    const jobId = this.job_details?.id;
    if (!jobId) {
      this.toastr.error('Job ID not found');
      return;
    }
    const formData = new FormData();
    formData.append('job_id', jobId);
    if (this.resumeFile) {
      formData.append('job_resume', this.resumeFile);
    }
    if (this.coverFile) {
      formData.append('job_cover_letter', this.coverFile);
    }
    this.ngxService.start();
    this.TeacherJobsService.quickApplyJobAPI(formData).subscribe({
      next: (res: any) => {
        this.ngxService.stop();
        if (res.status) {
          this.toastr.success(res.message || 'Applied successfully!');
          this.router.navigate(['/teacher/jobs'], { queryParams: { tab: 'all' } });
        } else {
          this.toastr.error(res.message || 'Failed to apply');
        }
      },
      error: (err: any) => {
        this.ngxService.stop();
        this.toastr.error(err.message || 'Failed to apply');
      }
    });
  }
  ApplyExternal() {
    const jobId = this.job_details?.id;
    if (!jobId) {
      this.toastr.error('Job ID not found');
      return;
    }
    const formData = new FormData();
    formData.append('job_id', jobId);
    if (this.resumeFile) {
      formData.append('job_resume', this.resumeFile);
    }
    if (this.coverFile) {
      formData.append('job_cover_letter', this.coverFile);
    }
    this.ngxService.start();
    this.TeacherJobsService.applyJobAPI(formData).subscribe({
      next: (res: any) => {
        this.ngxService.stop();
      }
    });

    let url = this.job_details.apply_through_site;
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      window.open(url, '_blank');
    }

  }
  ApplyNow() {
    const jobId = this.job_details?.id;
    if (!jobId) {
      this.toastr.error('Job ID not found');
      return;
    }


    const formData = new FormData();
    formData.append('job_id', jobId);
    if (this.resumeFile) {
      formData.append('job_resume', this.resumeFile);
    }
    if (this.coverFile) {
      formData.append('job_cover_letter', this.coverFile);
    }
    this.ngxService.start();
    this.TeacherJobsService.applyJobAPI(formData).subscribe({
      next: (res: any) => {
        this.ngxService.stop();
      }
    });
  }
  signup() {
    // Open signup in a new tab and pass jobId as param only
    const jobId = this.job_details?.id || this.jobId;
    let url = '/signup';
    if (jobId) {
      url += `?job_id=${jobId}`;
    }
    window.open(url);
  }
  login() {
    // Open login in a new tab and pass jobId as param only
    const jobId = this.job_details?.id || this.jobId;
    let url = '/login';
    if (jobId) {
      url += `?job_id=${jobId}`;
    }
    window.open(url);
  }
}
